import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { ModalResult } from '../models';

enum Size {
  small = 'sm',
  medium = 'lg',
  large = 'xl',
  xl = 'full',
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly modalService = inject(NgbModal);

  modals: NgbModalRef[] = [];

  private config = {
    backdrop: 'static',
    keyboard: false,
    // Applies Bootstrap's .modal-dialog-scrollable to every modal, so a dialog taller than the
    // window scrolls its body instead of running off the bottom of the screen. Without it, a
    // content-heavy modal (Stock Enquiry from the sales order line product link is the usual
    // example) was simply unreachable below the fold on a smaller display - and with a static
    // backdrop it could not be dismissed by clicking away either.
    scrollable: true,
  } as NgbModalOptions;

  get modalLevel(): number {
    return this.modals.length;
  }

  openSmall<T>(component: any, windowClass?: string): ModalResult<T> {
    return this.open(component, Size.small, windowClass);
  }

  openMedium<T>(component: any, windowClass?: string): ModalResult<T> {
    return this.open(component, Size.medium, windowClass);
  }

  openLarge<T>(component: any, windowClass?: string): ModalResult<T> {
    return this.open(component, Size.large, windowClass);
  }

  openXL<T>(component: any, windowClass?: string): ModalResult<T> {
    return this.open(component, Size.xl, windowClass);
  }

  close(data?: any) {
    this.modal.close(data);
    this.removeModal();
  }

  dismiss(data?: any) {
    this.modal.dismiss(data);
    this.removeModal();
  }

  get anyModalsOpen(): boolean {
    return this.modalService.hasOpenModals();
  }

  private open<T>(component: any, size: Size, windowClass?: string): ModalResult<T> {
    this.modals.push(this.modalService.open(component, { ...this.config, size, windowClass }));
    return { modal: this.modal, component: this.modal.componentInstance };
  }

  private get modal() {
    return this.modals[this.modals.length - 1];
  }

  private removeModal() {
    this.modals = this.modals.slice(0, this.modals.length - 1);
  }
}
